import React, { useState, useEffect, useCallback } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Shield,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Info,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronRight,
  Target,
  Layers,
  Activity,
  Eye,
  Zap,
  Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CFSP-1 Constraint Analysis Panel
 * 
 * Applies the Constraint-First Specification Protocol v1 to analyze
 * state machines as constraint architectures.
 * 
 * Displays: Control Score, Failure Modes, Topology, Strata, Recommendations
 */
const CFSP1Panel = ({ machineId }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    failures: true,
    topology: false,
    strata: false,
    axioms: false,
  });

  const runAnalysis = useCallback(async () => {
    if (!machineId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API_URL}/api/machines/${machineId}/cfsp1`);
      if (!resp.ok) throw new Error('Analysis failed');
      const data = await resp.json();
      setAnalysis(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [machineId]);

  useEffect(() => {
    runAnalysis();
  }, [runAnalysis]);

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!machineId) {
    return (
      <div className="p-4 text-center text-gray-500" data-testid="cfsp1-empty">
        <Shield className="w-6 h-6 mx-auto mb-2 opacity-50" />
        <p className="text-xs">Select a machine for CFSP-1 analysis</p>
      </div>
    );
  }

  if (loading && !analysis) {
    return (
      <div className="p-4 text-center text-gray-500" data-testid="cfsp1-loading">
        <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
        <p className="text-xs">Running constraint analysis...</p>
      </div>
    );
  }

  if (error && !analysis) {
    return (
      <div className="p-4 text-center text-red-400" data-testid="cfsp1-error">
        <AlertCircle className="w-6 h-6 mx-auto mb-2" />
        <p className="text-xs">{error}</p>
        <Button size="sm" variant="outline" onClick={runAnalysis} className="mt-2">
          Retry
        </Button>
      </div>
    );
  }

  if (!analysis) return null;

  return (
    <div className="h-full flex flex-col" data-testid="cfsp1-panel">
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-3">
          {/* Control Score */}
          <ControlScoreCard analysis={analysis} onRefresh={runAnalysis} loading={loading} />

          {/* Failure Modes */}
          <CollapsibleSection
            title="Failure Modes"
            icon={AlertTriangle}
            badge={analysis.failure_modes.length}
            badgeColor={analysis.failure_modes.some(f => f.severity === 'critical') ? 'destructive' : 'outline'}
            expanded={expandedSections.failures}
            onToggle={() => toggleSection('failures')}
          >
            <FailureModesList modes={analysis.failure_modes} />
          </CollapsibleSection>

          {/* Topology */}
          <CollapsibleSection
            title="Topology"
            icon={Activity}
            expanded={expandedSections.topology}
            onToggle={() => toggleSection('topology')}
          >
            <TopologyCard topology={analysis.topology} />
          </CollapsibleSection>

          {/* Strata */}
          <CollapsibleSection
            title="Stratum Hierarchy"
            icon={Layers}
            expanded={expandedSections.strata}
            onToggle={() => toggleSection('strata')}
          >
            <StrataCard strata={analysis.strata} />
          </CollapsibleSection>

          {/* Axiom Compliance */}
          <CollapsibleSection
            title="Axiom Compliance"
            icon={CheckCircle}
            expanded={expandedSections.axioms}
            onToggle={() => toggleSection('axioms')}
          >
            <AxiomCard axioms={analysis.axiom_compliance} />
          </CollapsibleSection>

          {/* Recommendations */}
          {analysis.recommendations.length > 0 && (
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
              <h4 className="text-[10px] font-mono text-blue-400 mb-2 uppercase tracking-wider">Recommendations</h4>
              <div className="space-y-1.5">
                {analysis.recommendations.map((rec, i) => (
                  <div key={i} className="flex items-start space-x-2 text-[11px] text-gray-300">
                    <Target className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>{rec}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};


/* --- Sub-components --- */

const ControlScoreCard = ({ analysis, onRefresh, loading }) => {
  const score = analysis.control_score;
  const comp = analysis.comprehensiveness;
  const coh = analysis.coherence;

  const getScoreColor = (s) => {
    if (s >= 0.8) return 'text-green-400';
    if (s >= 0.6) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getBarColor = (s) => {
    if (s >= 0.8) return 'bg-green-500';
    if (s >= 0.6) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const criticalCount = analysis.failure_modes.filter(f => f.severity === 'critical').length;
  const warningCount = analysis.failure_modes.filter(f => f.severity === 'warning').length;

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-3" data-testid="cfsp1-control-score">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Shield className="w-4 h-4 text-cyan-400" />
          <span className="text-xs font-mono text-gray-400 uppercase tracking-wider">CFSP-1</span>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onRefresh}
          disabled={loading}
          className="h-6 w-6 p-0"
        >
          <RefreshCw className={cn("w-3 h-3", loading && "animate-spin")} />
        </Button>
      </div>

      {/* Control Score */}
      <div className="text-center mb-3">
        <div className={cn("text-3xl font-bold font-mono", getScoreColor(score))}>
          {Math.round(score * 100)}
        </div>
        <div className="text-[10px] text-gray-500 uppercase tracking-wider">Control Score</div>
      </div>

      {/* Comprehensiveness vs Coherence */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <div className="flex items-center justify-between text-[10px] mb-1">
            <span className="text-gray-500">Comprehensive</span>
            <span className={cn("font-mono", getScoreColor(comp))}>{Math.round(comp * 100)}%</span>
          </div>
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className={cn("h-full rounded-full transition-all", getBarColor(comp))} style={{ width: `${comp * 100}%` }} />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between text-[10px] mb-1">
            <span className="text-gray-500">Coherence</span>
            <span className={cn("font-mono", getScoreColor(coh))}>{Math.round(coh * 100)}%</span>
          </div>
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className={cn("h-full rounded-full transition-all", getBarColor(coh))} style={{ width: `${coh * 100}%` }} />
          </div>
        </div>
      </div>

      {/* Failure summary pills */}
      <div className="flex items-center justify-center space-x-2">
        {criticalCount > 0 && (
          <Badge variant="destructive" className="text-[10px] h-5 px-2">
            {criticalCount} critical
          </Badge>
        )}
        {warningCount > 0 && (
          <Badge variant="outline" className="text-[10px] h-5 px-2 text-yellow-400 border-yellow-500/30">
            {warningCount} warning
          </Badge>
        )}
        {criticalCount === 0 && warningCount === 0 && (
          <Badge variant="outline" className="text-[10px] h-5 px-2 text-green-400 border-green-500/30">
            No failure modes
          </Badge>
        )}
      </div>
    </div>
  );
};


const CollapsibleSection = ({ title, icon: Icon, badge, badgeColor, expanded, onToggle, children }) => (
  <div className="border border-white/10 rounded-lg overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-3 py-2 bg-white/5 hover:bg-white/10 transition-colors"
    >
      <div className="flex items-center space-x-2">
        <Icon className="w-3.5 h-3.5 text-gray-400" />
        <span className="text-xs font-medium">{title}</span>
        {badge !== undefined && badge > 0 && (
          <Badge variant={badgeColor || 'outline'} className="text-[10px] h-4 px-1.5">
            {badge}
          </Badge>
        )}
      </div>
      {expanded ? <ChevronDown className="w-3 h-3 text-gray-500" /> : <ChevronRight className="w-3 h-3 text-gray-500" />}
    </button>
    {expanded && <div className="p-3 bg-black/20">{children}</div>}
  </div>
);


const FailureModesList = ({ modes }) => {
  if (modes.length === 0) {
    return (
      <div className="text-center py-3 text-xs text-green-400">
        <CheckCircle className="w-5 h-5 mx-auto mb-1" />
        No failure modes detected
      </div>
    );
  }

  const severityIcon = {
    critical: <AlertCircle className="w-3.5 h-3.5 text-red-400" />,
    warning: <AlertTriangle className="w-3.5 h-3.5 text-yellow-400" />,
    info: <Info className="w-3.5 h-3.5 text-blue-400" />,
  };

  const severityBorder = {
    critical: 'border-red-500/30 bg-red-500/5',
    warning: 'border-yellow-500/30 bg-yellow-500/5',
    info: 'border-blue-500/30 bg-blue-500/5',
  };

  return (
    <div className="space-y-2">
      {modes.map((mode, i) => (
        <div key={i} className={cn("border rounded-lg p-2.5", severityBorder[mode.severity])} data-testid={`cfsp1-failure-${mode.f_id}`}>
          <div className="flex items-start space-x-2">
            {severityIcon[mode.severity]}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <Badge variant="outline" className="text-[10px] h-4 px-1.5 font-mono">{mode.f_id}</Badge>
                <span className="text-[11px] font-medium truncate">{mode.name}</span>
              </div>
              <p className="text-[10px] text-gray-400 leading-relaxed">{mode.description}</p>
              <div className="mt-1.5 flex items-center space-x-2">
                <Badge variant="outline" className="text-[9px] h-3.5 px-1 text-gray-500">{mode.stratum}</Badge>
              </div>
              {mode.affected.length > 0 && (
                <div className="mt-1.5 space-y-0.5">
                  {mode.affected.slice(0, 3).map((a, j) => (
                    <div key={j} className="text-[9px] text-gray-500 font-mono truncate">{a}</div>
                  ))}
                  {mode.affected.length > 3 && (
                    <div className="text-[9px] text-gray-600">+{mode.affected.length - 3} more</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};


const TopologyCard = ({ topology }) => {
  const stats = [
    { label: 'States', value: topology.total_states, icon: Layers },
    { label: 'Transitions', value: topology.total_transitions, icon: Activity },
    { label: 'Events', value: topology.total_events, icon: Zap },
    { label: 'Reachable', value: topology.reachable_states, icon: Eye },
    { label: 'SCCs', value: topology.strongly_connected_components, icon: RefreshCw },
    { label: 'Diameter', value: topology.diameter, icon: Target },
  ];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        {stats.map(({ label, value, icon: Icon }) => (
          <div key={label} className="bg-white/5 rounded p-2 text-center">
            <Icon className="w-3 h-3 mx-auto mb-1 text-gray-500" />
            <div className="text-sm font-mono font-bold">{value}</div>
            <div className="text-[9px] text-gray-500">{label}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between text-[10px] px-1">
        <span className="text-gray-500">Density</span>
        <span className="font-mono">{(topology.density * 100).toFixed(1)}%</span>
      </div>

      <div className="flex items-center justify-between text-[10px] px-1">
        <span className="text-gray-500">Cycles</span>
        <span className={cn("font-mono", topology.has_cycles ? "text-cyan-400" : "text-gray-500")}>
          {topology.has_cycles ? 'Yes' : 'No'}
        </span>
      </div>

      {topology.unreachable_states.length > 0 && (
        <div className="bg-red-500/5 border border-red-500/20 rounded p-2">
          <div className="text-[10px] text-red-400 mb-1">Unreachable States</div>
          {topology.unreachable_states.map(s => (
            <div key={s} className="text-[10px] text-gray-400 font-mono">{s}</div>
          ))}
        </div>
      )}

      {topology.dead_end_states.length > 0 && (
        <div className="bg-yellow-500/5 border border-yellow-500/20 rounded p-2">
          <div className="text-[10px] text-yellow-400 mb-1">Dead-End States</div>
          {topology.dead_end_states.map(s => (
            <div key={s} className="text-[10px] text-gray-400 font-mono">{s}</div>
          ))}
        </div>
      )}
    </div>
  );
};


const STRATUM_CONFIG = [
  { key: 'ontological', label: 'ONTOLOGICAL', color: 'text-red-400 border-red-500/30 bg-red-500/10', desc: 'What exists' },
  { key: 'topological', label: 'TOPOLOGICAL', color: 'text-orange-400 border-orange-500/30 bg-orange-500/10', desc: 'Valid configurations' },
  { key: 'dynamical', label: 'DYNAMICAL', color: 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10', desc: 'Evolution rules' },
  { key: 'epistemic', label: 'EPISTEMIC', color: 'text-green-400 border-green-500/30 bg-green-500/10', desc: 'Knowledge constraints' },
  { key: 'strategic', label: 'STRATEGIC', color: 'text-blue-400 border-blue-500/30 bg-blue-500/10', desc: 'Agent actions' },
  { key: 'tactical', label: 'TACTICAL', color: 'text-purple-400 border-purple-500/30 bg-purple-500/10', desc: 'Implementation' },
];

const StrataCard = ({ strata }) => (
  <div className="space-y-2">
    {STRATUM_CONFIG.map(({ key, label, color, desc }) => {
      const items = strata[key] || [];
      if (items.length === 0) return null;
      return (
        <div key={key} className={cn("border rounded p-2", color)}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-mono font-bold">{label}</span>
            <span className="text-[9px] opacity-70">{desc}</span>
          </div>
          <div className="space-y-0.5">
            {items.slice(0, 4).map((item, i) => (
              <div key={i} className="text-[9px] text-gray-400 font-mono truncate">
                {item.element}
              </div>
            ))}
            {items.length > 4 && (
              <div className="text-[9px] text-gray-600">+{items.length - 4} more</div>
            )}
          </div>
        </div>
      );
    })}
  </div>
);


const AxiomCard = ({ axioms }) => {
  const axiomLabels = {
    'A0.1_constraint_prior': { label: 'A0.1', desc: 'Constraint is prior to content' },
    'A0.2_collapse_solution_space': { label: 'A0.2', desc: 'Constraints collapse solution space' },
    'A0.3_architecture_durable': { label: 'A0.3', desc: 'Architecture more durable than outputs' },
    'A0.4_coherence_measurable': { label: 'A0.4', desc: 'Coherence is measurable' },
    'A0.5_invisible_constraints': { label: 'A0.5', desc: 'Most powerful constraint = reality' },
    'A0.6_definer_controls': { label: 'A0.6', desc: 'Definer controls solution space' },
  };

  return (
    <div className="space-y-1.5">
      {Object.entries(axioms).map(([key, passed]) => {
        const info = axiomLabels[key] || { label: key, desc: '' };
        return (
          <div key={key} className="flex items-center space-x-2 text-[11px]">
            {passed ? (
              <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
            )}
            <span className="font-mono text-gray-400 w-8">{info.label}</span>
            <span className={cn("flex-1", passed ? "text-gray-300" : "text-red-300")}>{info.desc}</span>
          </div>
        );
      })}
    </div>
  );
};


export default CFSP1Panel;
